import React, { useState, useEffect, useCallback } from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import TopBar from "../components/Topbar";
import OnboardingChecklist from "../components/OnboardingChecklist";
import Sidebar from "../components/Sidebar";
import CallNotification from "../components/CallNotification";
import FutureContactDueReminder from "../components/FutureContactDueReminder";
import { useWorkspaceConfig } from "../context/WorkspaceConfigContext";
import { useSocketContext } from "../context/SocketContext";
import { useCallContext } from "../context/CallContext";
import { useOnboarding } from "../context/OnboardingContext";
import { useNotificationsOptional } from "../context/NotificationContext";
import { useEffectivePermissions, toSet, canAny } from "../hooks/useEffectivePermissions";
import { fetchData, getConversationById } from "../api/client";
import { useLazyI18nNamespaces } from "../i18n/useLazyI18nNamespaces";

const MainLayout = ({ children }) => {
  // const [collapsed, setCollapsed] = useState(false);
  const [collapsed, setCollapsed] = useState(
      () => JSON.parse(localStorage.getItem("collapsed")) || false
  );
  const [isMobileOpen, setMobileOpen] = useState(false);
  const [showSetupInvite, setShowSetupInvite] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); 
  const location = useLocation();
  const navigate = useNavigate();
  useLazyI18nNamespaces(location.pathname);
  const { isLoading: workspaceLoading, companyKey } = useWorkspaceConfig();
  const { state: onboardingState, markStepCompleted } = useOnboarding();
  const { socket } = useSocketContext();
  const { clearIncomingCall, setIncomingCallFromSocket } = useCallContext();
  const notificationsCtx = useNotificationsOptional();

  useEffect(() => {
    if (!socket) return;
    const handleCallEnded = () => clearIncomingCall();
    socket.on("callEnded", handleCallEnded);
    return () => socket.off("callEnded", handleCallEnded);
  }, [socket, clearIncomingCall]);

  // newCall: escutado no layout para o CallNotification abrir em qualquer página (ex.: ao simular em Configurações > Canais)
  useEffect(() => {
    if (!socket || !setIncomingCallFromSocket) return;
    const handleNewCall = async (payload) => {
      try {
        const raw =
          typeof payload === "object" && payload !== null
            ? payload.protocol ?? payload.chatId ?? payload.id
            : payload;
        const protocolId = raw != null ? Number(raw) : null;
        if (!protocolId || Number.isNaN(protocolId)) return;
        const res = await getConversationById(protocolId);
        const dto = res?.data || res;
        if (!dto) return;
        const client = dto.client || {};
        const conversation = {
          id: dto.id,
          name: client.name ?? dto.name ?? "",
          phoneNumber: client.phone ?? null,
          lastMessage: dto.lastMessage || "",
          timestamp: dto.timestamp,
          channel: dto.channel,
          status: dto.status,
          assignedTo: dto.userName ?? dto.assignedTo ?? null,
          unread: dto.unread || 0,
          statusLabel: "Em Atendimento",
        };
        setIncomingCallFromSocket({
          conversation,
          onAccept: () => {
            navigate("/chat", { state: { openConversationId: conversation.id } });
          },
          onReject: () => {},
        });
      } catch (error) {
        console.error("[MainLayout] Erro ao processar newCall:", error);
      }
    };
    socket.on("newCall", handleNewCall);
    return () => socket.off("newCall", handleNewCall);
  }, [socket, setIncomingCallFromSocket, navigate]);

  useEffect(() => {
    if (!socket || !notificationsCtx?.dispatchFromSocketLegacy) return;
    const handler = (payload) => notificationsCtx.dispatchFromSocketLegacy(payload);
    socket.on("notification", handler);
    return () => socket.off("notification", handler);
  }, [socket, notificationsCtx]);

  const { data: permData, isLoading: permsLoading } = useEffectivePermissions();
  const scopesSet = toSet(permData?.scopes);
  const canConfigureWorkspace = canAny(scopesSet, [
    ["companies", "view"],
    ["channels", "view"],
    ["users", "view"],
    ["types", "view"],
    ["automation_rules", "view"],
  ]);

  // Controla o Drawer mobile do Sidebar.
  // Se `nextOpen` for booleano, força o estado; caso contrário, alterna.
  const toggleDrawer = useCallback((nextOpen) => {
    if (typeof nextOpen === "boolean") {
      setMobileOpen(nextOpen);
      return;
    }
    setMobileOpen((prev) => !prev);
  }, []);

  useEffect(() => {
      // Atualiza o localStorage sempre que o collapsed muda
      localStorage.setItem("collapsed", JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    if (workspaceLoading || permsLoading) return;
    if (!companyKey) return;
    if (onboardingChecked) return;

    // Evita exibir em rotas específicas (ex.: página de setup)
    if (location.pathname.startsWith("/setup")) return;

    const optOutKey = `workspace-setup-opt-out-${companyKey}`;
    const doneKey = `workspace-setup-done-${companyKey}`;
    const seenKey = `workspace-setup-seen-${companyKey}`;
    const visitedKey = `workspace-setup-visited-${companyKey}`;

    let cancelled = false;

    const checkAndMaybeShow = async () => {
      try {
        const optedOut = localStorage.getItem(optOutKey) === "1";
        if (optedOut) {
          return;
        }

        const finishedSetup = localStorage.getItem(doneKey) === "1";
        if (finishedSetup) {
          return;
        }

        // 1) Se o onboard principal já foi concluído via contexto, não exibe o convite
        const hasCompletedCoreOnboarding =
          Array.isArray(onboardingState.completedSteps) &&
          onboardingState.completedSteps.includes("core-onboarding-done");
        if (hasCompletedCoreOnboarding) {
          localStorage.setItem(doneKey, "1");
          return;
        }

        // 2) Verifica se o usuário (admin/master) já passou pelo setup ao menos uma vez
        const hasVisitedSetup = localStorage.getItem(visitedKey) === "1";

        // 3) Heurística: se já existem EMPRESAS, CANAIS ou TIPOS
        // cadastrados, consideramos que o workspace já foi minimamente
        // parametrizado e não exibimos mais o convite.
        const resources = [
          { key: "companies", endpoint: "/companies?page=1&limit=1" },
          { key: "channels", endpoint: "/channels?page=1&limit=1" },
          { key: "types", endpoint: "/types?page=1&limit=1" },
        ];

        const results = await Promise.all(
          resources.map((r) =>
            fetchData(r.endpoint, "GET").catch(() => null)
          )
        );

        if (cancelled) return;

        const summary = resources.reduce((acc, resMeta, idx) => {
          const res = results[idx];
          if (!res) {
            acc[resMeta.key] = false;
            return acc;
          }
          const dataArray = Array.isArray(res.data) ? res.data : [];
          const hasArrayItems = dataArray.length > 0;
          const hasTotal =
            typeof res.totalInDatabase === "number" &&
            res.totalInDatabase > 0;
          acc[resMeta.key] = hasArrayItems || hasTotal;
          return acc;
        }, {});

        const hasAnyConfigured =
          summary.companies || summary.channels || summary.types;

        if (hasAnyConfigured) {
          try {
            localStorage.setItem(doneKey, "1");
          } catch {
            // falha silenciosa
          }
          // Opcionalmente marcamos o onboarding principal como concluído
          try {
            markStepCompleted("core-onboarding-done");
          } catch {
            // falha silenciosa
          }
          return;
        }

        // 4) Se NÃO há nada configurado e o usuário PODE configurar,
        // redireciona automaticamente para o setup no primeiro acesso.
        if (!hasAnyConfigured && !hasVisitedSetup && canConfigureWorkspace) {
          navigate("/setup", { replace: true });
          return;
        }

        // 5) Se o usuário não pode configurar (atendente) e o workspace ainda não
        // foi parametrizado, não mostramos o popup de checklist – apenas saímos.
        if (!canConfigureWorkspace && !hasAnyConfigured) {
          return;
        }

        const alreadySeenThisSession =
          sessionStorage.getItem(seenKey) === "1";
        if (alreadySeenThisSession) return;

        // Só mostra o checklist se o usuário já visitou o setup uma vez
        if (hasVisitedSetup) {
          sessionStorage.setItem(seenKey, "1");
          setShowSetupInvite(true);
        }
      } catch {
        // Em caso de erro com storage ou fetch, evita travar a tela;
        // mostra o convite apenas uma vez nesta sessão.
        try {
          const alreadySeenThisSession =
            sessionStorage.getItem(seenKey) === "1";
          if (!alreadySeenThisSession) {
            sessionStorage.setItem(seenKey, "1");
            setShowSetupInvite(true);
          }
        } catch {
          setShowSetupInvite(true);
        }
      } finally {
        if (!cancelled) {
          setOnboardingChecked(true);
        }
      }
    };

    checkAndMaybeShow();

    return () => {
      cancelled = true;
    };
  }, [
    workspaceLoading,
    companyKey,
    location.pathname,
    onboardingChecked,
    onboardingState.completedSteps,
    markStepCompleted,
  ]);

  const handleGoToSetup = () => {
    setShowSetupInvite(false);
    navigate("/setup");
  };

  const handleAdvancedMode = () => {
    if (companyKey) {
      try {
        localStorage.setItem(`workspace-setup-opt-out-${companyKey}`, "1");
      } catch {
        // falha silenciosa
      }
    }
    setShowSetupInvite(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* TopBar */}
      <Box
        sx={{
          flexShrink: 0,
          height: "64px", // Altura fixa do TopBar
        }}
      >
        <TopBar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          toggleDrawer={toggleDrawer}
        />
      </Box>

      <CallNotification />
      <FutureContactDueReminder />

      {/* Conteúdo principal */}
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        {/* Sidebar com largura fixa */}
        <Box
          className="sidebar-scroll"
          sx={{
            overflowX: "hidden",
            flexShrink: 0, // Sidebar não diminui de tamanho
            width: isMobile ? 0 : collapsed ? "70px" : "210px", // Ajuste de largura
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            transition: "width 0.3s ease", // Transição suave
            height: "calc(100vh - 65px)",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Sidebar
            collapsed={collapsed}
            isMobileOpen={isMobileOpen}
            toggleDrawer={toggleDrawer}
          />
        </Box>

        {/* Conteúdo principal com rolagem */}
        <Box
          className="sidebar-scroll tour-main-content"
          component="main"
          sx={{
            flexGrow: 1, // Faz o conteúdo principal ocupar o espaço restante
            overflowY: "auto", // Permite rolagem do conteúdo principal
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            padding: "5px", // Margem interna
            minWidth: "0", // Previne quebra no layout
            transition: "all 0.3s ease",
            paddingBottom: "50px",
            height: "calc(100vh - 64px)",
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Onboarding principal (checklist guiado) */}
      <OnboardingChecklist
        open={showSetupInvite}
        onClose={() => setShowSetupInvite(false)}
      />

    </Box>
  );
};

export default MainLayout;
